import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Fade from '@mui/material/Fade';
import FormContainer from './FormContainer';
import ThankYou from '../../components/ThankYou';
import { makeStyles } from '@mui/styles';
import { Designs } from 'modules/connect-card/constants/designTemplates';
import Typography from '@mui/material/Typography';
import { request, getAccountLogoUrl } from '../../utils';
import { getFormValues } from '../../utils/formUtils';
import ErrorModal from '../../components/ErrorModal';
import VideoPlayer from 'components/VideoPlayer';
import { Trans } from 'react-i18next';
import Link from '@mui/material/Link';
import { WEBSITE } from 'constants/config';

const useStyles = makeStyles((theme) => {
  return {
    root: () => {
      return {
        'display': 'flex',
        'flexDirection': 'column',
        'alignItems': 'center',
        '& .progress': {
          marginTop: theme.spacing(2),
          [theme.breakpoints.down('sm')]: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
          },
        },
      };
    },
    logoBox: {
      'margin': 'auto',
      'display': 'flex',
      'alignItems': 'center',
      'justifyContent': 'center',
      'width': 140,
      'height': 60,
      'marginBottom': theme.spacing(2),
      '& img': {
        objectFit: 'contain',
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: '100%',
      },
      [theme.breakpoints.up('sm')]: {
        width: 180,
        maxHeight: 80,
      },
    },
    innerContainer: {
      position: 'relative',
      maxWidth: 700,
      width: '100%',
    },
  };
});

const ConnectCard = ({
  isWidget,
  data,
  contact: existingContact,
  account,
  questions,
  onSubmitComplete,
  apiUrl,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [contact, setContact] = useState(existingContact);
  const [error, setError] = useState();
  const [customFields, setCustomFields] = useState([]);
  const gaRef = useRef();
  const pixelRef = useRef();
  const {
    id,
    card_name,
    card_headline,
    card_description,
    logo_or_video_url,
    logo_or_video,
    card_template,
    pixel_id,
    google_analytics_id,
    group_id,
  } = data;

  const classes = useStyles({
    designTemplate: Designs[card_template] || Designs['1'],
    templateId: card_template,
    isWidget,
  });

  useEffect(() => {
    const API_URL = apiUrl || global.APP_CONFIG.REACT_APP_API_URL;
    if (google_analytics_id) {
      const { default: ReactGA } = require('react-ga');
      ReactGA.initialize(google_analytics_id);
      gaRef.current = ReactGA;
    }
    if (pixel_id) {
      const { default: ReactPixel } = require('react-facebook-pixel');
      if (ReactPixel.init) {
        ReactPixel.init(pixel_id);
        pixelRef.current = ReactPixel;
      }
    }
    request(`${API_URL}API/1_0/connectCard.php`, {
      method: 'POST',
      body: JSON.stringify({
        hash: data.hash,
        counter: 'card_visits',
        change: '+1',
      }),
    }).catch((e) => {
      console.log(e);
    });

    request(
      `${API_URL}API/1_0/contactCustomColumns.php?account_hash=${account.account_hash}`,
      {
        method: 'GET',
      },
    ).then((resp) => {
      setCustomFields(resp);
    });
  }, []);

  const saveSubmission = async (data) => {
    const API_URL = apiUrl || global.APP_CONFIG.REACT_APP_API_URL;
    return await request(`${API_URL}API/1_0/connectCardSubmission.php`, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        connect_card_id: id,
      }),
    });
  };

  const createContact = async (values) => {
    const API_URL = apiUrl || global.APP_CONFIG.REACT_APP_API_URL;
    const nameField = values.find((i) => i.is_first_name === '1');
    const emailField = values.find((i) => i.is_email_address === '1');
    const phoneField = values.find((i) => i.is_mobile === '1');

    if (emailField.valueString || phoneField.valueString) {
      return await request(`${API_URL}API/1_0/contact.php`, {
        method: 'POST',
        body: JSON.stringify({
          hash: contact?.hash,
          account_id: data.account_id,
          contact_first_name: nameField.value.firstName,
          contact_last_name: nameField.value.lastName,
          contact_email: emailField?.value,
          primary_phone: phoneField?.value,
          contact_source: 'Connect Card',
          contact_source_id: data.id,
        }),
      });
    } else {
      throw new Error('Email or phone is required');
    }
  };

  const handleSmartSave = async (values, contact) => {
    const API_URL = apiUrl || global.APP_CONFIG.REACT_APP_API_URL;
    const contactUpdate = {};
    const comments = [];
    for (const field of values) {
      let { question_settings } = field;
      if (typeof question_settings === 'string') {
        try {
          question_settings = JSON.parse(question_settings);
        } catch (e) {
          question_settings = {};
        }
      } else if (!question_settings) {
        question_settings = {};
      }
      const { smartSave, smartSaveField } = question_settings;
      if (smartSave && smartSaveField) {
        const [fieldType, fieldId] = smartSaveField.split('.');
        function handleContactField() {
          switch (fieldId) {
            case 'name':
            case 'contact_email':
            case 'primary_phone': {
              break;
            }
            case 'address': {
              contactUpdate.contact_address1 = field.value.address1;
              contactUpdate.contact_city = field.value.city;
              contactUpdate.contact_state = field.value.state;
              contactUpdate.contact_zip = field.value.zip;
              break;
            }
            case 'contact_comments': {
              comments.push(`${field.question_label}: ${field.valueString}`);
              break;
            }
            case 'contact_dob': {
              if (field.valueString !== null) {
                contactUpdate.contact_dob = field.valueString;
              }
              break;
            }
          }
        }

        async function handleCustomField() {
          const customField = customFields.find(
            (i) => i.column_name === fieldId,
          );
          if (customField && field.value !== null) {
            const customFieldUpdate = {
              contact_id: contact.contact_id,
              column_value: field.valueString,
              contact_custom_column_id: customField.column_id,
            };

            await request(`${API_URL}API/1_0/contactCustomColumnsValues.php`, {
              method: 'POST',
              body: JSON.stringify(customFieldUpdate),
            });
          }
        }

        switch (fieldType) {
          case 'contact': {
            if (contact) {
              handleContactField();
            }
            break;
          }
          case 'custom': {
            await handleCustomField();
          }
        }
      }
    }

    if (comments.length) {
      contactUpdate.contact_comments = comments.join('\n');
    }

    if (contact && Object.keys(contactUpdate).length) {
      await request(`${API_URL}API/1_0/contact.php`, {
        method: 'POST',
        body: JSON.stringify({
          hash: contact.hash,
          ...contactUpdate,
        }),
      });
    }
  };

  const saveAnswer = async (field, contact, submission) => {
    const API_URL = apiUrl || global.APP_CONFIG.REACT_APP_API_URL;
    await request(`${API_URL}API/1_0/connectCardAnswer.php`, {
      method: 'POST',
      body: JSON.stringify({
        contact_id: contact?.contact_id,
        connect_card_question_id: field.id,
        answer: JSON.stringify({ value: field.value }),
        connect_card_submission_id: submission.connect_card_submission_id,
      }),
    });
  };

  const handleSubmit = async (values) => {
    const formValues = getFormValues(values, questions);
    const API_URL = apiUrl || global.APP_CONFIG.REACT_APP_API_URL;
    setError(null);
    try {
      const newContact = await createContact(formValues);
      const submission = await saveSubmission({
        contact_id: newContact.contact_id,
        submission_completed: 0,
      });
      setContact(newContact);
      if (newContact && group_id && group_id !== '0') {
        await request(`${API_URL}API/1_0/groupContact.php`, {
          method: 'POST',
          body: JSON.stringify({
            contact_hash: newContact.hash,
            group_id,
            group_contact_source: 'Connection card',
          }),
        }).catch((e) => {
          console.log(e);
        });
      }

      await Promise.all(
        formValues
          .filter(
            (i) =>
              !(i.value === '' || i.value === null || i.value === undefined),
          )
          .map((i) => saveAnswer(i, newContact, submission)),
      );

      await handleSmartSave(formValues, newContact).catch((e) =>
        console.error(e),
      );

      await saveSubmission({
        hash: submission.hash,
        submission_completed: 1,
      }).catch((e) => console.error(e));

      request(`${API_URL}API/1_0/connectCard.php`, {
        method: 'POST',
        body: JSON.stringify({
          hash: data.hash,
          counter: 'card_completions',
          change: '+1',
        }),
      }).catch((e) => {
        console.error(e);
      });
      if (pixelRef.current) {
        pixelRef.current.track('Lead', {
          content_name: card_name,
        });
      }
      if (gaRef.current) {
        gaRef.current.event({
          category: 'Lead',
          action: 'Form Submit',
        });
      }
      onSubmitComplete && onSubmitComplete(values);
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const accountLogoUrl = getAccountLogoUrl(account);
  const logoUrl = logo_or_video_url || accountLogoUrl;
  const hasVideo = logo_or_video === 'video' && logo_or_video_url;

  const renderLogo = () => {
    let url = logoUrl;
    if (hasVideo) {
      url = accountLogoUrl;
    }
    if (!url) {
      return (
        <Typography
          paragraph
          textAlign="center"
          fontWeight={700}
          variant="subtitle2"
          color="primary.main"
        >
          {account.account_name}
        </Typography>
      );
    }
    return (
      <Box className={classes.logoBox}>
        <img src={url} />
      </Box>
    );
  };

  return (
    <Fade in timeout={800}>
      <Box px={2} pb={5}>
        {!hasVideo && renderLogo()}
        <div>
          {submitted ? (
            <ThankYou
              contact={contact}
              settings={data}
              logoUrl={logo_or_video === 'video' ? accountLogoUrl : logoUrl}
            />
          ) : (
            <>
              <Stack spacing={1}>
                {hasVideo && <VideoPlayer url={logo_or_video_url} />}
              </Stack>
              <Box mb={2}>
                {card_headline && (
                  <Typography
                    gutterBottom
                    textAlign="center"
                    variant="subtitle1"
                    fontWeight={600}
                  >
                    {card_headline}
                  </Typography>
                )}
                {card_description && (
                  <Typography
                    gutterBottom
                    sx={{ px: 1 }}
                    fontWeight={500}
                    textAlign="center"
                  >
                    {card_description}
                  </Typography>
                )}
                {data.card_type === 'multistep' &&
                  contact?.contact_first_name && (
                    <Typography textAlign="center" color="text.secondary">
                      Hi {contact.contact_first_name}!
                    </Typography>
                  )}
              </Box>
              <FormContainer
                account={account}
                data={data}
                contact={contact}
                questions={questions}
                onSubmit={handleSubmit}
              />
              <Box maxWidth={600} mx="auto" mt={3}>
                <Typography
                  paragraph
                  variant="caption"
                  component="p"
                  textAlign="center"
                  lineHeight={1.2}
                >
                  <Trans ns="dcc" i18nKey="footer">
                    We will not share your contact info. By clicking submit you
                    agree to receive communications from{' '}
                    {{ church_name: account.account_name }} (frequency varies).
                    Message and data rates may apply. Text STOP at any time to
                    cancel or text HELP for help.
                  </Trans>{' '}
                  <Link target="_blank" href={`${WEBSITE}/privacy-policy`}>
                    Privacy Policy
                  </Link>{' '}
                  &{' '}
                  <Link target="_blank" href={`${WEBSITE}/terms-of-use`}>
                    Terms of Use
                  </Link>{' '}
                  apply.
                </Typography>
                <Typography textAlign="center" variant="caption" component="p">
                  Powered by{' '}
                  <Link
                    target="_blank"
                    href="https://textinchurch.com"
                    style={{ fontWeight: 500, color: 'inherit' }}
                  >
                    Text In Church
                  </Link>
                </Typography>
              </Box>
            </>
          )}
          {error && <ErrorModal message={error} onClose={() => setError()} />}
        </div>
      </Box>
    </Fade>
  );
};

export default ConnectCard;
